<template>
  <div class="dash">
    <div class="page-title">Dashboard</div>
    <div class="page-sub">Welcome back — here's what's happening today.</div>

    <!-- Stat Cards -->
    <div class="grid-4">
      <div class="stat-card" style="--c:#F26522;--ib:rgba(242,101,34,.1)">
        <div class="stat-top">
          <div class="stat-label">Total Societies</div>
          <div class="stat-icon"><v-icon color="#F26522" size="18">mdi-office-building-outline</v-icon></div>
        </div>
        <div class="stat-val">{{ stats.totalSocieties }}</div>
        <div class="stat-desc">Registered societies</div>
      </div>
      <div class="stat-card" style="--c:#378add;--ib:rgba(55,138,221,.1)">
        <div class="stat-top">
          <div class="stat-label">Total Members</div>
          <div class="stat-icon"><v-icon color="#378add" size="18">mdi-account-multiple-outline</v-icon></div>
        </div>
        <div class="stat-val">{{ stats.totalMembers }}</div>
        <div class="stat-desc">Across all societies</div>
      </div>
      <div class="stat-card" style="--c:#1d9e75;--ib:rgba(29,158,117,.1)">
        <div class="stat-top">
          <div class="stat-label">Total Admins</div>
          <div class="stat-icon"><v-icon color="#1d9e75" size="18">mdi-shield-account-outline</v-icon></div>
        </div>
        <div class="stat-val">{{ stats.totalAdmins }}</div>
        <div class="stat-desc">Super admins</div>
      </div>
      <div class="stat-card" style="--c:#ba7517;--ib:rgba(186,117,23,.1)">
        <div class="stat-top">
          <div class="stat-label">Society Requests</div>
          <div class="stat-icon"><v-icon color="#ba7517" size="18">mdi-clipboard-text-clock-outline</v-icon></div>
        </div>
        <div class="stat-val">{{ stats.societyRequests }}</div>
        <div class="stat-desc">Pending approval</div>
      </div>
    </div>

    <!-- Charts Row -->
    <div class="grid-2">
      <div class="card">
        <div class="chart-header">
          <div>
            <div class="section-title" style="margin-bottom:2px">Payment Overview</div>
            <div class="chart-sub">Monthly revenue trend</div>
          </div>
        </div>
        <div class="chart-legend"><span class="legend-line" style="background:#1d9e75"></span>Revenue (₹)</div>
        <div style="position:relative;width:100%;height:180px">
          <canvas ref="payChart"></canvas>
        </div>
      </div>
      <div class="card">
        <div class="chart-header">
          <div>
            <div class="section-title" style="margin-bottom:2px">Society Registrations</div>
            <div class="chart-sub">New societies per month</div>
          </div>
        </div>
        <div class="chart-legend"><span class="legend-bar" style="background:#F26522"></span>Registrations</div>
        <div style="position:relative;width:100%;height:180px">
          <canvas ref="socChart"></canvas>
        </div>
      </div>
    </div>

    <!-- Society Status + Categories -->
    <div class="grid-5-7">
      <div class="card">
        <div class="section-title">Society status</div>
        <div class="donut-wrap">
          <div style="position:relative;width:140px;height:140px;flex-shrink:0">
            <canvas ref="donutChart"></canvas>
          </div>
          <div class="legend">
            <div class="legend-row" v-for="s in filteredStatus" :key="s.label">
              <span class="ldot" :style="{ background: s.color }"></span>
              <span class="llabel">{{ s.label }}</span>
              <span class="lval">{{ s.value }}</span>
            </div>
          </div>
        </div>
      </div>
      <div class="card">
        <div class="section-title">Society categories</div>
          <div class="cat-row" v-for="cat in categoryBars" :key="cat.label">
          <div class="cat-top">
            <div class="cat-left">
              <div class="cat-ico" :style="{ background: cat.iconBg }">
                <v-icon :color="cat.color" size="14">{{ cat.icon }}</v-icon>
              </div>
              {{ cat.label }}
            </div>
            <div class="cat-right">
              <div class="cat-val-group">
                <span class="cat-count">{{ cat.count }}</span>
                <span class="cat-unit">Societies</span>
              </div>
              <div class="cat-pct-tag" :style="{ background: cat.iconBg, color: cat.color }">{{ cat.pct }}%</div>
            </div>
          </div>
          <div class="bar-bg">
            <div class="bar-fill" :style="{ width: cat.pct + '%', background: cat.color }"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Top Societies + Recent Feedbacks -->
    <div class="grid-6-6 unified-card">
      <div class="card-half">
        <div class="list-header">
          <div class="section-title" style="margin:0">Top societies</div>
          <router-link to="/superadmin/society/list" class="view-all">View all →</router-link>
        </div>
        <div class="soc-list">
          <div
            class="soc-row"
            v-for="(soc) in topSocieties"
            :key="soc.id"
            @click="$router.push('/superadmin/society/list/' + soc.id + '/details')"
            style="cursor:pointer;"
          >
            <PremiumAvatar 
              :name="soc.name" 
              :image="soc.logo_url ? API_BASE_URL.replace('/api','') + '/uploads/' + soc.logo_url : null"
              :size="32" 
              variant="squircle" 
            />
            <div class="soc-info">
              <div class="soc-name">{{ soc.name }}</div>
              <div class="soc-meta">{{ soc.city || '' }} · {{ soc.member_count }} members</div>
            </div>
            <div class="soc-r">
              <span class="ism-badge ism-badge-green">Active</span>
            </div>
          </div>
          <div v-if="!topSocieties.length && !loading" class="empty-state">No data yet</div>
        </div>
      </div>
      <div class="card-half divider-l">
        <div class="list-header">
          <div class="section-title" style="margin:0">Recent feedbacks</div>
          <router-link to="/superadmin/feedback" class="view-all">View all →</router-link>
        </div>
        <div class="fb-list">
          <div
            class="fb-row"
            v-for="fb in recentFeedbacks"
            :key="fb.id"
            @click="openFeedback(fb)"
            style="cursor:pointer;"
          >
            <PremiumAvatar 
              :name="fb.member ? fb.member.name : (fb.member_id ? 'Anonymous Member' : 'Society Admin')" 
              :image="fb.member && fb.member.profile_url ? (API_BASE_URL.replace('/api','') + '/uploads/' + fb.member.profile_url) : null"
              :size="32" 
            />
            <div class="fb-info">
              <div class="fb-name">{{ fb.member ? fb.member.name : (fb.member_id ? 'Anonymous Member' : 'Society Admin') }}</div>
              <div class="fb-meta">{{ formatDateShort(fb.created_at) }}</div>
            </div>
            <div class="fb-r">
              <span class="ism-badge" :class="statusBadgeClass(fb.status)">
                {{ statusLabel(fb.status) }}
              </span>
            </div>
          </div>
          <div v-if="!recentFeedbacks.length && !loading" class="empty-state">No feedbacks yet</div>
        </div>
      </div>
    </div>


    <!-- Feedback Detail Popup -->
    <PremiumModalForm
      v-model="feedbackDialog"
      title="Feedback Detail"
      submitText="Update Status"
      cancelText="Close"
      :loading="updatingStatus"
      max-width="520"
      @submit="updateStatus"
    >
      <div v-if="activeFeedback" style="display:flex;flex-direction:column;gap:14px;">

        <div class="ism-field-group">
          <label class="ism-field-label">Title</label>
          <div class="ism-field-value">{{ activeFeedback.title || '—' }}</div>
        </div>

        <div class="ism-field-group">
          <label class="ism-field-label">Description</label>
          <div class="ism-field-value" style="white-space:pre-wrap;">{{ activeFeedback.description || '—' }}</div>
        </div>

        <div class="ism-field-row">
          <div class="ism-field-group">
            <label class="ism-field-label">Reported By</label>
            <div class="ism-field-value">
              {{ activeFeedback.member ? activeFeedback.member.name : (activeFeedback.society ? activeFeedback.society.name : (activeFeedback.member_id ? 'Anonymous Member' : 'Society Admin')) }}
            </div>
          </div>
          <div class="ism-field-group">
            <label class="ism-field-label">Date</label>
            <div class="ism-field-value">{{ formatDateShort(activeFeedback.created_at) }}</div>
          </div>
        </div>

        <div class="ism-field-group">
          <label class="ism-field-label">Status</label>
          <v-select
            v-model="activeFeedback.status"
            :items="statusOptions"
            item-text="label"
            item-value="value"
            outlined
            dense
            hide-details
            color="#F26522"
          />
        </div>

        <div v-if="activeFeedback.picture_url" class="ism-field-group">
          <label class="ism-field-label">Attachment</label>
          <div v-if="imgError" class="ism-img-placeholder">
            <v-icon size="32" color="var(--text-secondary)">mdi-image-off-outline</v-icon>
            <span>Image not found</span>
          </div>
          <img
            v-else
            :src="API_BASE_URL.replace('/api','') + '/uploads/' + activeFeedback.picture_url"
            class="ism-feedback-img"
            @error="imgError = true"
            @click="imgDialog = true"
          />
        </div>

      </div>
    </PremiumModalForm>

    <!-- Premium Image Lightbox -->
    <PremiumImageLightbox
      v-model="imgDialog"
      :imageSrc="activeFeedback ? (API_BASE_URL.replace('/api','') + '/uploads/' + activeFeedback.picture_url) : ''"
      :title="activeFeedback ? activeFeedback.title : 'Feedback Image'"
    />
  </div>
</template>

<script>
import { mapMutations } from 'vuex';
import PremiumAvatar from '../common/PremiumAvatar.vue';
import PremiumModalForm from '../common/PremiumModalForm.vue';
import PremiumImageLightbox from '../common/PremiumImageLightbox.vue';
import { useFilterStore } from '@/pinia/filterStore';
import moment from 'moment';

export default {
  name: 'SuperAdminDashboard',
  components: { PremiumAvatar, PremiumModalForm, PremiumImageLightbox },

  data() {
    return {
      loading: false,
      stats: { totalSocieties: 0, totalMembers: 0, totalAdmins: 0, societyRequests: 0 },
      societyStatus: [],
      categories: { residential: 0, commercial: 0, both: 0, total: 1 },
      recentFeedbacks: [],
      topSocieties: [],
      payChartLabels: [],
      payChartData: [],
      socChartLabels: [],
      socChartData: [],
      payChartInstance: null,
      socChartInstance: null,
      donutChartInstance: null,
      feedbackDialog: false,
      activeFeedback: null,
      updatingStatus: false,
      imgError: false,
      imgDialog: false,
      API_BASE_URL: process.env.VUE_APP_API_URL || 'https://api-staging.ismartsociety.com/api',

      statusOptions: [
        { label: 'Pending',   value: 1 },
        { label: 'Declined',  value: 2 },
        { label: 'Completed', value: 3 },
      ],
    };
  },

  computed: {
    filteredStatus() {
      return this.societyStatus
        .filter(s => s.label === 'Active' || s.label === 'Inactive')
        .map(s => ({
          ...s,
          color: s.label === 'Active' ? '#1d9e75' : (s.label === 'Inactive' ? '#e24b4a' : s.color)
        }));
    },
    categoryBars() {
      const t = this.categories.total || 1;
      return [
        { label: 'Residential', count: this.categories.residential, pct: Math.round(this.categories.residential / t * 100), color: '#378add', iconBg: 'rgba(55,138,221,.1)', icon: 'mdi-home-outline' },
        { label: 'Commercial',  count: this.categories.commercial,  pct: Math.round(this.categories.commercial  / t * 100), color: '#F26522', iconBg: 'rgba(242,101,34,.1)',  icon: 'mdi-store-outline' },
        { label: 'Both',        count: this.categories.both,        pct: Math.round(this.categories.both        / t * 100), color: '#534ab7', iconBg: 'rgba(83,74,183,.1)',   icon: 'mdi-domain' },
      ];
    },
    filterStore() { return useFilterStore(); }
  },

  methods: {
    ...mapMutations('alert', ['setAlert']),
    rankClass(i) { return ['', 'rank-1', 'rank-2', 'rank-3'][i + 1] || ''; },
    formatDate(d) { return moment(d).format('DD MMM, YYYY'); },
    formatDateShort(d) { return moment(d).format('DD MMM, YYYY'); },
    openFeedback(fb) { 
      this.activeFeedback = { ...fb }; 
      this.imgError = false; 
      this.feedbackDialog = true; 
    },
    statusLabel(s) {
      return { 1: 'Pending', 2: 'Declined', 3: 'Completed' }[s] || '—';
    },
    statusBadgeClass(s) {
      return { 1: 'ism-badge-amber', 2: 'ism-badge-red', 3: 'ism-badge-green' }[s] || 'ism-badge-gray';
    },

    updateStatus() {
      if (!this.activeFeedback) return;
      this.updatingStatus = true;
      this.$axios.put('superadmin/feedback/' + this.activeFeedback.id, { 
        status: this.activeFeedback.status 
      }).then(res => {
        this.updatingStatus = false;
        if (res.data.status) {
          this.setAlert({ showAlert: true, variant: 'success', timeout: 3000, message: res.data.message || 'Status updated successfully' });
          this.feedbackDialog = false;
          this.fetchAll();
        } else {
          this.setAlert({ showAlert: true, variant: 'red accent-3', timeout: 3000, message: res.data.message });
        }
      }).catch(() => { this.updatingStatus = false; });
    },

    async fetchAll() {
      this.loading = true;
      try {
        const [overview, payment, registrations] = await Promise.all([
          this.$axios.get('superadmin/dashboardOverview'),
          this.$axios.get('superadmin/paymentOverview'),
          this.$axios.get('superadmin/societyRegistrations'),
        ]);

        if (overview.data.status) {
          this.stats          = overview.data.stats;
          this.societyStatus  = overview.data.societyStatus;
          this.categories     = overview.data.categories;
          this.recentFeedbacks = Array.isArray(overview.data.recentFeedbacks)
            ? overview.data.recentFeedbacks
            : (overview.data.recentFeedbacks.rows || []);
          this.topSocieties   = overview.data.topSocieties;
        }
        if (payment.data.status) {
          this.payChartLabels = payment.data.labels;
          this.payChartData   = payment.data.data;
        }
        if (registrations.data.status) {
          this.socChartLabels = registrations.data.labels;
          this.socChartData   = registrations.data.data;
        }
      } catch (e) { console.error(e); }
      this.loading = false;
      this.$nextTick(() => this.buildCharts());
    },

    buildCharts() {
      const Chart = window.Chart; // eslint-disable-line
      if (!Chart) return;
      const gridC = 'rgba(148,163,184,0.12)';
      const tickC = '#94a3b8';

      // Shared Premium Tooltip Configuration
      const tooltipConfig = {
        enabled: true,
        backgroundColor: 'rgba(15, 23, 42, 0.95)',
        titleColor: '#ffffff',
        titleFont: { size: 12, weight: '700', family: "'Inter', sans-serif" },
        bodyColor: '#cbd5e1',
        bodyFont: { size: 12, family: "'Inter', sans-serif" },
        padding: 12,
        cornerRadius: 10,
        displayColors: true,
        boxWidth: 8,
        boxHeight: 8,
        boxPadding: 8,
        usePointStyle: true,
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1,
        caretSize: 6,
      };

      // Payment chart
      if (this.payChartInstance) this.payChartInstance.destroy();
      if (this.$refs.payChart) {
        this.payChartInstance = new Chart(this.$refs.payChart, {
          type: 'line',
          data: {
            labels: this.payChartLabels,
            datasets: [{ label: 'Revenue (₹)', data: this.payChartData, borderColor: '#1d9e75', backgroundColor: 'rgba(29,158,117,0.07)', borderWidth: 2, pointRadius: 2.5, pointHoverRadius: 4, pointBackgroundColor: '#1d9e75', tension: 0.45, fill: true }]
          },
          options: { 
            responsive: true, 
            maintainAspectRatio: false, 
            plugins: { 
              legend: { display: false },
              tooltip: {
                ...tooltipConfig,
                callbacks: {
                  label: (context) => ` Revenue: ₹${context.parsed.y.toLocaleString()}`
                }
              }
            }, 
            scales: { 
              x: { grid: { color: gridC }, ticks: { color: tickC, font: { size: 10 } } }, 
              y: { grid: { color: gridC }, ticks: { color: tickC, font: { size: 10 }, callback: v => '₹' + (v >= 1000 ? (v / 1000).toFixed(0) + 'k' : v) }, beginAtZero: true } 
            },
            interaction: {
              intersect: false,
              mode: 'index',
            }
          }
        });
      }

      // Registrations chart
      if (this.socChartInstance) this.socChartInstance.destroy();
      if (this.$refs.socChart) {
        this.socChartInstance = new Chart(this.$refs.socChart, {
          type: 'bar',
          data: {
            labels: this.socChartLabels,
            datasets: [{ label: 'Registrations', data: this.socChartData, backgroundColor: 'rgba(242,101,34,0.7)', borderRadius: 5, borderSkipped: false }]
          },
          options: { 
            responsive: true, 
            maintainAspectRatio: false, 
            plugins: { 
              legend: { display: false },
              tooltip: tooltipConfig
            }, 
            scales: { 
              x: { grid: { color: gridC }, ticks: { color: tickC, font: { size: 10 } } }, 
              y: { grid: { color: gridC }, ticks: { color: tickC, font: { size: 10 } }, beginAtZero: true } 
            } 
          }
        });
      }

      // Donut chart
      if (this.donutChartInstance) this.donutChartInstance.destroy();
      if (this.$refs.donutChart && this.societyStatus.length) {
        this.donutChartInstance = new Chart(this.$refs.donutChart, {
          type: 'doughnut',
          data: {
            labels: this.societyStatus.map(s => s.label),
            datasets: [{ 
              data: this.societyStatus.map(s => s.value), 
              backgroundColor: this.societyStatus.map(s => {
                if (s.label === 'Active') return '#1d9e75';
                if (s.label === 'Inactive') return '#e24b4a';
                return s.color;
              }), 
              borderWidth: 0, 
              hoverOffset: 8 
            }]
          },
          options: { 
            cutout: '72%', 
            responsive: true, 
            maintainAspectRatio: true, 
            plugins: { 
              legend: { display: false },
              tooltip: tooltipConfig
            } 
          }
        });
      }
    },
  },

  mounted() {
    // ── Reset Filters ──
    // Navigating to dashboard counts as a 'module switch' which clears other filters
    this.filterStore.updateFilter('dashboard', {});

    // Load Chart.js if not already loaded
    if (typeof window.Chart === 'undefined') {
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/Chart.js/4.4.1/chart.umd.js';
      script.onload = () => this.fetchAll();
      document.head.appendChild(script);
    } else {
      this.fetchAll();
    }
  },

  beforeDestroy() {
    if (this.payChartInstance)   this.payChartInstance.destroy();
    if (this.socChartInstance)   this.socChartInstance.destroy();
    if (this.donutChartInstance) this.donutChartInstance.destroy();
  },
};
</script>

<style scoped>
.dash { padding: 0 0 32px; }
.page-title { font-size: 20px; font-weight: 600; color: var(--text-primary); margin-bottom: 2px; font-family: "Inter", sans-serif; }
.page-sub { font-size: 13px; color: var(--text-secondary); margin-bottom: 20px; font-family: "Inter", sans-serif; }
.section-title { font-size: 13.5px; font-weight: 600; color: var(--text-primary); margin-bottom: 14px; font-family: "Inter", sans-serif; }
.chart-sub { font-size: 11px; color: var(--text-secondary); }

/* Grids */
.grid-4   { display: grid; grid-template-columns: repeat(4, minmax(0,1fr)); gap: 10px; margin-bottom: 14px; }
.grid-2   { display: grid; grid-template-columns: repeat(2, minmax(0,1fr)); gap: 12px; margin-bottom: 14px; }
.grid-5-7 { display: grid; grid-template-columns: 5fr 7fr; gap: 12px; margin-bottom: 14px; }
.grid-6-6 { display: grid; grid-template-columns: 1fr 1fr; margin-bottom: 14px; }

.unified-card {
  background: var(--card-bg);
  border: 0.5px solid var(--border-color);
  border-radius: 12px;
  overflow: hidden;
}
.card-half { padding: 16px; flex: 1; display: flex; flex-direction: column; }
.divider-l { border-left: 0.5px solid var(--border-color); }

/* Card */
.card {
  background: var(--card-bg);
  border: 0.5px solid var(--border-color);
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 0;
}

/* Stat card */
.stat-card {
  background: var(--card-bg);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  padding: 14px 16px;
  position: relative;
  overflow: hidden;
  transition: all 0.2s ease;
}
.stat-card:hover {
  border-color: var(--c) !important;
  transform: translateY(-2px);
  box-shadow: var(--shadow-md);
}
.stat-card::before { content: ''; position: absolute; bottom: 0; left: 0; right: 0; height: 3px; background: var(--c); }
.stat-top { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 10px; }
.stat-label { font-size: 12px; color: var(--text-secondary); font-weight: 500; font-family: "Inter", sans-serif; }
.stat-icon { width: 32px; height: 32px; border-radius: 8px; display: flex; align-items: center; justify-content: center; background: var(--ib); flex-shrink: 0; }
.stat-val { font-size: 26px; font-weight: 600; color: var(--text-primary); line-height: 1; font-family: "Inter", sans-serif; }
.stat-desc { font-size: 11px; color: var(--text-secondary); margin-top: 4px; font-family: "Inter", sans-serif; }

/* Chart header */
.chart-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; }
.chart-legend { display: flex; align-items: center; gap: 6px; font-size: 11px; color: var(--text-secondary); margin-bottom: 10px; }
.legend-line { display: inline-block; width: 18px; height: 3px; border-radius: 2px; }
.legend-bar  { display: inline-block; width: 10px; height: 10px; border-radius: 2px; }

/* Donut */
.donut-wrap { display: flex; align-items: center; gap: 20px; }
.legend { display: flex; flex-direction: column; gap: 8px; flex: 1; }
.legend-row { display: flex; align-items: center; gap: 8px; font-size: 12px; }
.ldot { width: 9px; height: 9px; border-radius: 50%; flex-shrink: 0; }
.llabel { flex: 1; color: var(--text-secondary); }
.lval { font-weight: 600; color: var(--text-primary); }

/* Category bars */
.cat-row { margin-bottom: 13px; }
.cat-row:last-child { margin-bottom: 0; }
.cat-top { display: flex; align-items: center; justify-content: space-between; margin-bottom: 6px; }
.cat-left { display: flex; align-items: center; gap: 8px; font-size: 13px; color: var(--text-primary); font-weight: 500; }
.cat-ico { width: 26px; height: 26px; border-radius: 6px; display: flex; align-items: center; justify-content: center; font-size: 13px; flex-shrink: 0; }
.cat-right { display: flex; align-items: center; gap: 12px; }
.cat-val-group { display: flex; flex-direction: column; align-items: flex-end; line-height: 1; }
.cat-count { font-size: 14px; font-weight: 700; color: var(--text-primary); }
.cat-unit { font-size: 9px; text-transform: uppercase; color: var(--text-secondary); font-weight: 600; letter-spacing: 0.02em; margin-top: 2px; }
.cat-pct-tag { font-size: 11px; font-weight: 700; padding: 4px 8px; border-radius: 6px; min-width: 38px; text-align: center; }
.bar-bg { height: 6px; background: var(--bg-primary); border: 0.5px solid var(--border-color); border-radius: 99px; overflow: hidden; }
.bar-fill { height: 100%; border-radius: 99px; transition: width 0.4s ease; }

/* Lists */
.list-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 14px; }
.view-all { font-size: 11px; color: #378add; text-decoration: none; font-weight: 500; }

/* Top Societies */
.soc-list { display: flex; flex-direction: column; gap: 8px; }
.soc-row { 
  display: flex; align-items: center; gap: 10px; padding: 9px 10px; 
  border-radius: 8px; background: var(--bg-primary); 
  border: 0.5px solid var(--border-color);
  transition: all 0.2s ease;
}
.soc-row:hover {
  border-color: var(--accent) !important;
  background: var(--card-bg);
  transform: translateY(-1px);
}
.rank { width: 20px; height: 20px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 10px; font-weight: 600; flex-shrink: 0; background: var(--card-bg); color: var(--text-secondary); border: 0.5px solid var(--border-color2); }
.rank-1 { background: #faeeda; color: #854f0b; border-color: #fac775; }
.rank-2 { background: #f1efe8; color: #5f5e5a; border-color: #d3d1c7; }
.rank-3 { background: #faece7; color: #993c1d; border-color: #f5c4b3; }
.soc-info { flex: 1; overflow: hidden; }
.soc-name { font-size: 13px; font-weight: 600; color: var(--text-primary); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.soc-meta { font-size: 11px; color: var(--text-secondary); }
.soc-r { display: flex; flex-direction: column; align-items: flex-end; gap: 3px; }
.chip { font-size: 10px; padding: 2px 7px; border-radius: 20px; font-weight: 600; }
.chip-a { background: #e1f5ee; color: #0f6e56; }
.chip-t { background: #faece7; color: #993c1d; }
.plan-tag { font-size: 10px; color: var(--text-secondary); }

/* Feedbacks */
.fb-list { display: flex; flex-direction: column; gap: 8px; }
.fb-row { 
  display: flex; align-items: center; gap: 10px; padding: 9px 10px; 
  border-radius: 8px; background: var(--bg-primary); 
  border: 0.5px solid var(--border-color);
  transition: all 0.2s ease;
}
.fb-row:hover {
  border-color: var(--accent) !important;
  background: var(--card-bg);
  transform: translateY(-1px);
}
.fb-info { flex: 1; overflow: hidden; }
.fb-name { font-size: 13px; font-weight: 600; color: var(--text-primary); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.fb-meta { font-size: 11px; color: var(--text-secondary); }
.fb-r { display: flex; flex-direction: column; align-items: flex-end; gap: 3px; }

/* Feedback detail popup fields */
.ism-field-group { display: flex; flex-direction: column; gap: 5px; }
.ism-field-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.ism-field-label {
  font-size: 10.5px; font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.07em; color: var(--text-secondary, #64748b);
  font-family: "Inter", sans-serif;
}
.ism-field-value {
  font-size: 13.5px; color: var(--text-primary, #1e293b);
  background: var(--bg-primary, #f1f5f9);
  border: 1px solid var(--border-color, rgba(0,0,0,0.07));
  border-radius: 8px; padding: 10px 14px; line-height: 1.5;
  font-family: "Inter", sans-serif;
  min-height: 42px;
}

.ism-feedback-img {
  width: 100%; max-height: 200px; object-fit: cover;
  border-radius: 10px; cursor: pointer;
  border: 1px solid var(--border-color, rgba(0,0,0,0.07));
  transition: opacity 0.18s;
}
.ism-feedback-img:hover { opacity: 0.85; }

.ism-img-placeholder {
  width: 100%; height: 160px; background: var(--bg-primary);
  border: 1px dashed var(--border-color); border-radius: 12px;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 8px; color: var(--text-secondary); font-size: 12px; font-weight: 500;
}

/* Quick Actions */
.quick-grid { display: grid; grid-template-columns: repeat(5, minmax(0,1fr)); gap: 8px; }
.q-btn { display: flex; align-items: center; gap: 8px; padding: 10px 12px; border-radius: 8px; border: 0.5px solid var(--border-color); background: var(--bg-primary); text-decoration: none; cursor: pointer; transition: background 0.15s; }
.q-btn:hover { background: var(--card-bg); border-color: rgba(242,101,34,0.3); }
.q-ico { width: 28px; height: 28px; border-radius: 6px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.q-label { font-size: 12px; color: var(--text-primary); font-weight: 500; font-family: "Inter", sans-serif; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.empty-state { font-size: 12px; color: var(--text-secondary); text-align: center; padding: 16px; }

@media (max-width: 900px) {
  .grid-4, .quick-grid { grid-template-columns: repeat(2, 1fr); }
  .grid-5-7, .grid-6-6, .grid-2 { grid-template-columns: 1fr; }
}
</style>
